import React from 'react';
import { Alert, Button, Card, Switch } from 'antd';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import TableList from './tables/TableList';
import useOnSiteTables from './tables/useOnSiteTables';
import {
  TableOrderingNote,
  isTableOrderingDirty,
  tableOrderingNote,
  tableOrderingSwitches,
} from './tableOrderingState';

/**
 * Order Methods → Table ordering (PROD-724, before: Room/Table service). The same two switches as
 * Pickup, saved with Save, which is off until a switch changes; then the restaurant's tables, each
 * with its own link and QR code. The shop offers table ordering only when both switches are on and
 * at least one table is on.
 */
const VISIBLE_SWITCH_ID = 'table-ordering-visible';

export default function TableOrderingTab({
  data,
  feedback = null,
  loading = false,
  onChange,
  onSave,
  toggleMethodVisible,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Takeout:TableOrderingTab' });
  const { company } = useSelector((state) => state.currentCompany);
  const {
    tables,
    loading: tablesLoading,
    failed: tablesFailed,
    reload,
    showsMigratedNote,
    add,
    rename,
    setActive,
    remove,
    reorder,
  } = useOnSiteTables(company?.id);

  const switches = tableOrderingSwitches(data);
  const dirty = isTableOrderingDirty(data, company);
  // No note while the tables load or after they failed to: an empty list then says nothing.
  const note = tableOrderingNote(company, tables, tablesLoading || tablesFailed);
  // The hidden note's link: to the visibility switch, focused so it can be turned on right away.
  const goToVisibleSwitch = () => {
    const visibleSwitch = document.getElementById(VISIBLE_SWITCH_ID);
    visibleSwitch?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    visibleSwitch?.focus();
  };

  // The save's error on the switch, e.g. "At least one order method has to be enabled!".
  const switchError = feedback?.errors?.has_on_site ?? feedback?.errors?.has_room_service;

  return (
    <div className="mt-lg">
      <Card className="mb-md">
        <div className="text-base font-semibold mb-md">{t('state.title')}</div>
        <div className="mb-md flex items-center">
          <Switch id={VISIBLE_SWITCH_ID} checked={switches.visible} onChange={toggleMethodVisible} />
          <label htmlFor={VISIBLE_SWITCH_ID} className="my-auto ml-4 cursor-pointer">
            {t('state.visible')}
          </label>
        </div>
        <div className="mb-md flex items-center">
          <Switch id="has-on-site" checked={switches.on} onChange={(checked) => onChange('has_on_site', checked)} />
          <label htmlFor="has-on-site" className="my-auto ml-4 cursor-pointer">
            {t('state.on')}
          </label>
        </div>
        {switchError && (
          <div className="-mt-xs mb-md text-sm text-gray-500" data-testid="table-ordering-switch-error">
            {switchError}
          </div>
        )}
        {note === TableOrderingNote.Hidden && (
          <Alert
            type="warning"
            showIcon
            className="mb-md"
            title={t('notes.hidden.title')}
            description={
              <>
                {t('notes.hidden.description')}{' '}
                <Button type="link" className="h-auto p-0" onClick={goToVisibleSwitch}>
                  {t('notes.hidden.link')}
                </Button>
              </>
            }
            data-testid="table-ordering-hidden-note"
          />
        )}
        {note === TableOrderingNote.Off && (
          <Alert
            type="info"
            showIcon
            className="mb-md"
            title={t('notes.off.title')}
            description={t('notes.off.description')}
            data-testid="table-ordering-off-note"
          />
        )}
        {note === TableOrderingNote.NoActiveTable && (
          <Alert
            type="warning"
            showIcon
            className="mb-md"
            title={t('notes.no_active_table.title')}
            description={t('notes.no_active_table.description')}
            data-testid="table-ordering-no-table-note"
          />
        )}
        <Button type="primary" loading={loading} disabled={loading || !dirty} onClick={onSave}>
          {t('buttons.save')}
        </Button>
      </Card>
      <TableList
        tables={tables}
        loading={tablesLoading}
        failed={tablesFailed}
        onRetry={reload}
        restaurantName={company?.name}
        showMigratedNote={!tablesLoading && !tablesFailed && showsMigratedNote}
        onAdd={add}
        onRename={rename}
        onToggle={setActive}
        onDelete={remove}
        onReorder={reorder}
      />
    </div>
  );
}
